<template>
  <div class="tab-comments__container" v-show="loaded">
    <div v-if="disallowed" class="forbidden">{{ $tr('common.forbidden') }}</div>
    <div v-if="!disallowed" class="tab-comments__content">
      <VuePerfectScrollbar
        ref="answers__scrollbar"
        class="scroll__container"
        :settings="settings"
        id="comments-scroll"
      >
        <div class="tab-comments" id="tab-comments">
          <div class="comments-block">
            <div v-for="comment in comments" class="comment-block" :key="comment.id">
              <div class="user-avatar-holder">
                <div class="user-avatar">
                  <img v-if="depersonalization" src="/static/images/elements/man.png">
                  <img v-else :src="comment.user.logo || '/static/images/elements/man.png'">
                </div>
              </div>
              <div class="user-comment">
                <div class="name-block">
                  <span class="user-name">{{ fullName(comment.user.name, comment.user.surname) }}</span>
                  <span class="comment-at">
                    {{ getCreatedAtDate(comment) }}
                  </span>

                </div>

                <div v-if="comment.id === editing" class="comment-text">
                  <vtextarea class="new-comment_textarea"
                    :height="130"
                    :auto_height="true"
                    :hasMaxHeight="true"
                    :min_height="40"
                    :rows="1"
                    placeholder=""
                    v-model="newMessage"
                  />
                </div>
                <div v-else class="comment-text" v-html="linkify(comment.message)"></div>

                <div class="comment-actions" v-if="canEdit(comment)">
                  <a v-if="comment.id !== editing" href="#" class="comment-action" @click.prevent="startEditing(comment)">{{ $tr('common.edit') }}</a>
                  <a v-else href="#" class="comment-action" @click.prevent="updateComment(comment)">{{ $tr('common.save') }}</a>
                  <a v-if="comment.id !== editing" href="#" class="comment-action" @click.prevent="deleteComment(comment.id)">{{ $tr('common.delete') }}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VuePerfectScrollbar>

      <div class="new-comment" v-if="canAdd">
        <div class="new-comment__avatar">
          <img :src="currentUser.logo || '/static/images/elements/man.png'">
        </div>
        <div class="new-comment__field custom-field">
          <vtextarea class="new-comment_textarea"
            :height="130"
            :auto_height="true"
            :hasMaxHeight="true"
            :min_height="40"
            :rows="1"
            :placeholder="$tr('responses.leaveComment')"
            v-model="message"
          />
        </div>
        <vbutton
          label="actions.send"
          @click="sendComment"
          :disabled="messageIsEmpty" />
      </div>
    </div>
  </div>
</template>

<script>
  import { directive as VueClickAway } from 'vue-clickaway'
  import VuePerfectScrollbar from 'vue-perfect-scrollbar'
  import { linkify } from '@/_helpers/utils'

  import Moment from 'moment'

  export default {
    name: 'tab-comments',

    props: {
      comments: Array,
      responseId: String,
      canAdd: {
        type: Boolean,
        default: true
      },
      depersonalization: {
        type: Boolean,
        default: false
      }
    },

    components: {
      VuePerfectScrollbar
    },

    directives: {
      onClickAway: VueClickAway
    },

    data () {
      return {
        settings: {
          maxScrollbarLength: 600,
          wheelSpeed: 10,
          swipeEasing: true
        },
        message: '',
        newMessage: '',
        editing: '',
        needScroll: false,
        loaded: false
      }
    },

    watch: {
      comments () {
        if (this.needScroll) {
          this.needScroll = false
          // scroll to down after update list of comments
          this.$nextTick(this.scrollToEnd)
        }
      }
    },

    methods: {
      fetchComments() {
        this.$store.dispatch('response/fetchComments', this.responseId)
          .then(() => {
            this.loaded = true
          })
      },
      linkify(inputText) {
        return linkify(inputText);
      },
      scrollToEnd () {
        const scrollBar = this.$el.querySelector('#comments-scroll')
        scrollBar.scrollTop = scrollBar.scrollHeight
      },

      sendComment () {
        if (this.messageIsEmpty) {
          return
        }
        this.$store.dispatch('response/createComment', {id: this.responseId, message: this.message})
          .then(() => {
            if (window.gtag) {
              window.gtag({'event': 'response_comment'});
            }
          })
        this.message = ''
        this.needScroll = true
      },

      deleteComment (id) {
        this.$store.dispatch('response/deleteComment', {responseId: this.responseId, commentId: id})
      },

      startEditing (comment) {
        this.editing = comment.id
        this.newMessage = comment.message
      },

      updateComment (comment) {
        if (this.newMessage !== comment.message && this.newMessage !== '') {
          this.$store.dispatch('response/updateComment', {
            responseId: this.responseId,
            commentId: this.editing,
            message: this.newMessage
          })
        }
        this.editing = ''
      },

      getCreatedAtDate (comment) {
        const date = comment ? Moment(comment.created_at).toDate() : null

        return date ? this.$i18n.d(date, 'full') : ''
      },

      canEdit (comment) {
        if (!this.canAdd) {
          return false
        }
        return comment.user.email === this.currentUser.email
      },

      fullName(name, surname) {
        if (this.depersonalization) {
          return name + ' ' + surname.substr(0, 1) + '.';
        }

        return [name, surname].join(' ');
      }
    },

    computed: {
      disallowed () {
        return this.$store.state.response.commentsDisallow
      },

      currentUser () {
        return this.$store.getters['user/current'] || {}
      },

      messageIsEmpty () {
        return !this.message.trim()
      }
    },

    created() {
      this.fetchComments();
    },

    mounted () {
      this.needScroll = true
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../../styles/colors.scss";

  .scroll__container {
    position: relative;
    max-height: 427px;
    flex: 1;
  }

  .comment-text__input {
    box-sizing: border-box;
    width: 100%;
    height: 30px;
  }

  .comments-block {
    // margin-top: 30px;
    // margin-left: 15px;
    padding-bottom: 30px;
  }

  .comment-block {
    display: flex;
    padding-bottom: 0;
    font-size: 16px;
  }

  .comment-block:last-child {
    margin-bottom: 0;
  }

  .user-avatar-holder {
    width: 50px;
    height: 50px;
  }

  .comment-block .user-avatar-holder .user-avatar {
    border-radius: 50%;
    overflow: hidden;
  }

  .user-avatar {
    width: 50px;
    height: 50px;
    margin-top: 5px;
  }

  .user-avatar img {
    width: 100%;
    height: 100%;
  }

  .comment-block .user-comment {
    width: 85%;
    margin-left: 4px;
    padding: 8px 5px 1px 2px;
    border-radius: 6px;
  }

  .comment-actions {
    visibility: hidden;
    a {
      display: inline-block;
      font-size: 15px;
      color: #ccc;
    }
    a:first-child {
      margin-right: 15px;
    }
  }
  .comment-block:hover {
    .comment-actions {
      visibility: visible;
    }
  }

  .comment-action:last-child {
    margin-right: 0;
  }

  .comment-block .user-comment .name-block {
    margin-bottom: 4px;
  }

  .user-name {
    // font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 15px;
    line-height: 22px;
    color: #233346;
  }

  .name-block {
    margin-bottom: 5px;
  }

  .comment-block .user-comment .comment-text {
    color: #333;
  }

  .comment-text {
    overflow: hidden;
    margin-top: 5px;
    // font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 22px;
    color: #233346;
    word-break: break-all;
    white-space: pre-line;
  }

  .comment-block .user-comment .comment-at {
    display: inline-block;
    margin-left: 40px;
    // font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 22px;
    color: #9FAAB6;
  }

  .new-comment {
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    width: 100%;
    padding-top: 10px;
    border-top: 1px solid #D2D6E0;

    .vcv-button {
      text-transform: initial;
      font-size: 14px;
      font-weight: normal;
      margin-left: auto;
    }
  }

  .new-comment__avatar {
    width: 40px;
    min-width: 40px;
    height: 40px;
    min-height: 40px;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 10px;
  }

  .new-comment__avatar img {
    width: 100%;
    height: 100%;
  }

  .new-comment__field {
    flex: 1;
    margin-right: 10px;
  }

  .new-comment__field input {
    border-radius: 18px;
    width: 100%;
    height: 38px;
    font-style: normal;
    padding: 9px 15px;
    border-color: #D2D6E0;
    border: 1px solid #D2D6E0;
    box-sizing: border-box;
    border-radius: 30px;
    // font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 15px;
    line-height: 22px;
    color: #233346;

    &::placeholder {
      color: #9FAAB6;
    }
  }

  .tab-comments {
    height: 400px;
    margin-top: -4px;
  }

  .tab-comments__container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .forbidden {
      margin-top: 20px;
    }
  }

  .tab-comments__content {
    display: flex;
    flex-direction: column;
    max-height: 448px;
    height: 448px;
  }

  .tab-comments__container ::v-deep .vtextarea.new-comment_textarea {
    textarea {
      height: 40px;
      padding: 8px 15px;
      // font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      font-size: 15px;
      line-height: 22px;
      color: #233346;
      max-width: 585px;
      resize: none;
      border-radius: 2px;
      resize: none;
    }
  }

  @media screen and (max-height: 900px){
    .tab-comments {
      height: 400px;
    }
  }

  @media screen and (max-height: 800px){
    .tab-comments {
      height: 400px;
    }
  }

  @media screen and (max-height: 670px){
    .tab-comments {
      height: 400px;
    }
  }

  .top-sticky + .tab-comments__container {
    .tab-comments__content {
      padding-top: 30px;
    }
    .tab-comments {
      height: 431px;
    }

    @media screen and (max-height: 900px){
      .tab-comments {
        height: 431px;
      }
    }

    @media screen and (max-height: 800px){
      .tab-comments {
        height: 431px;
      }
    }

    @media screen and (max-height: 670px){
      .tab-comments {
        height: 431px;
      }
    }
  }
</style>
